import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { Search } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  InputGroup,
  InputGroupAddon,
  InputGroupInput
} from 'design-system';

import { getConversationLabel } from '@/features/ai-chat/lib/conversationLabel';

import HistoryRecord from './HistoryRecord/HistoryRecord';

import { useChatHistory } from './useChatHistory';

const ChatHistory = ({
  anchorEl,
  setAnchorEl
}: {
  anchorEl: HTMLElement | null;
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
}) => {
  const { conversations, handleClose } = useChatHistory(setAnchorEl);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!anchorEl) {
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- Reset search when dropdown closes.
      setSearchQuery('');
    }
  }, [anchorEl]);

  const filteredConversations = conversations
    .map((conversation, index) => ({ conversation, index }))
    .filter(({ conversation, index }) => {
      const displayName = getConversationLabel(conversation, index);
      return displayName.toLowerCase().includes(searchQuery.toLowerCase());
    });

  return (
    <DropdownMenu
      open={Boolean(anchorEl)}
      onOpenChange={nextOpen => {
        if (!nextOpen) handleClose();
      }}
    >
      <DropdownMenuContent
        align='end'
        anchor={anchorEl}
        className='w-80 p-0'
        positionerClassName='z-[var(--z-panel-floating)]'
      >
        <div className='p-1'>
          <InputGroup>
            <InputGroupAddon>
              <Search aria-hidden='true' />
            </InputGroupAddon>
            <InputGroupInput
              aria-label='Search chat history'
              placeholder='Search conversations'
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              onKeyDown={event => {
                if (!['Escape', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
                  event.stopPropagation();
                }
              }}
            />
          </InputGroup>
        </div>
        <div className='ml-2 text-[10px] text-gx-text-disabled'>CHAT HISTORY</div>
        {/* oxlint-disable sonarjs/no-nested-conditional -- Intentional three-way empty/no-matches/results conditional for the history dropdown's states. */}
        {conversations.length === 0 ? (
          <div className='flex h-[75px] items-center justify-center'>No history yet</div>
        ) : filteredConversations.length === 0 ? (
          <div className='flex h-[75px] items-center justify-center'>No matches</div>
        ) : (
          filteredConversations.map(({ conversation, index }) => (
            <HistoryRecord
              key={conversation.conversationId}
              conversation={conversation}
              handleClose={handleClose}
              index={index}
            />
          ))
        )}
        {/* oxlint-enable sonarjs/no-nested-conditional */}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ChatHistory;
